/**
 * HeadlineHero — the dashboard's newsroom front-page headline.
 *
 * V2.6 Worker-B (2026-04-20). Replaces the 5-equal-tile grid's emphasized
 * Revenue tile with a full-width story card where the headline number owns
 * the fold: label + YoY chip on one line, a single dominant value beneath,
 * and a one-line supporting sentence with the concrete delta + context.
 *
 * Self-contained and presentational — parent owns the fetch.
 */

import { MetricSparkline } from '../analytics/MetricSparkline'
import { YoYChip } from '../analytics/YoYChip'

export interface HeadlineHeroProps {
  /** Short upper-case label ("Revenue · up 17.3% YoY" style — label only). */
  label: string
  /** Pre-formatted headline value (e.g. "$627,618"). */
  value: string
  /**
   * Short supporting sentence: "+$92,414 vs NOS 2025 · 12,674 orders · 2
   * days remaining". Rendered below the value in muted type. `null`/empty
   * hides the line entirely.
   */
  deltaLabel?: string | null
  /**
   * YoY percent. `null`/`undefined`/NaN suppresses the chip (same rules
   * as YoYChip).
   */
  yoyPct?: number | null
  /** Optional trailing sparkline (≥2 points). */
  sparkline?: Array<{ value: number }>
  /** Skeleton shell while data fetches. */
  loading?: boolean
  'data-testid'?: string
}

export function HeadlineHero({
  label,
  value,
  deltaLabel,
  yoyPct,
  sparkline,
  loading = false,
  'data-testid': testId = 'dashboard-headline-hero',
}: HeadlineHeroProps) {
  if (loading) {
    return (
      <div
        data-testid={testId}
        data-state="loading"
        aria-busy="true"
        aria-label={`Loading ${label}`}
        className="relative overflow-hidden rounded-card border border-butler-200 bg-butler-50 p-6 shadow-card-soft dark:border-butler-700 dark:bg-butler-900/30 lg:p-8"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px] bg-butler-sparkle opacity-70"
        />
        <div className="h-3 w-40 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-4 h-14 w-64 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-4 h-3 w-72 animate-pulse rounded bg-hairline dark:bg-slate-700" />
      </div>
    )
  }

  const hasSpark =
    Array.isArray(sparkline) && sparkline.length >= 2

  return (
    <section
      data-testid={testId}
      data-state="ready"
      className="group relative overflow-hidden rounded-card border border-butler-200 bg-butler-50 p-6 shadow-card-soft transition hover:shadow-card dark:border-butler-700 dark:bg-butler-900/30 lg:p-8"
    >
      {/* Butler sparkle rule — celebratory marquee strip at top edge. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px] bg-butler-sparkle opacity-70 group-hover:opacity-100 transition-opacity"
      />
      <div className="flex flex-wrap items-center gap-3">
        <div
          data-testid={`${testId}-label`}
          className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-600 dark:text-butler-200"
        >
          {label}
        </div>
        <YoYChip pct={yoyPct ?? null} data-testid={`${testId}-yoy`} />
      </div>
      <div
        data-testid={`${testId}-value`}
        className="mt-3 font-display text-5xl md:text-6xl font-bold tracking-tight text-butler-800 tabular-nums dark:text-butler-50"
      >
        {value}
      </div>
      {deltaLabel ? (
        <div
          data-testid={`${testId}-delta`}
          className="mt-3 text-sm text-ink-soft dark:text-slate-400"
        >
          {deltaLabel}
        </div>
      ) : null}
      {hasSpark ? (
        <div data-testid={`${testId}-spark`} className="mt-4">
          <MetricSparkline
            data={sparkline as Array<{ value: number }>}
            ariaLabel={`${label} trailing trend`}
            height={44}
          />
        </div>
      ) : null}
    </section>
  )
}
